import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../utils/cn';

interface LogbookCopyButtonProps {
  value: string;
  label: string;
  className?: string;
}

/** Copies one identifier or coordinate string, with a short confirmation in place of a toast. */
export const LogbookCopyButton = ({ value, label, className }: LogbookCopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // A blocked clipboard is not worth an error state: the value is on screen and selectable.
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={copy}
      className={cn('btn-icon-sm', className)}
      title={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
      aria-label={`Copy ${label.toLowerCase()}`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};
